import clsx from 'clsx';
import Card from 'components/atoms/Card/Card';
import Copyable from 'components/atoms/Copyable/Copyable';
import Currency from 'components/atoms/Currency/Currency';
import Txt from 'components/atoms/Txt/Txt';
import { useRouter } from 'next/router';
import React from 'react';
import { graphql, useFragment } from 'react-relay';
import { getCurrencyFromLocale } from 'utils/locale';
import { ReferAFriend_referralsRef_qmee$key } from './__generated__/ReferAFriend_referralsRef_qmee.graphql';
import PopupIcon from './assets/popup.svg';
import classes from './ReferAFriend.module.scss';
import Button from 'components/atoms/Button/Button';

export type Props = {
  referralsRef: ReferAFriend_referralsRef_qmee$key;
  walletCurrency?: string | null;
  className?: string;
  onOpenMetrics?: (e: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => void;
};

const ReferAFriendFragment = graphql`
  fragment ReferAFriend_referralsRef_qmee on Query {
    referralHistory {
      lifetime {
        successes
        earnings {
          subUnitAmount
          currency
        }
      }
    }
    referralLink
  }
`;

export default function ReferAFriend({
  referralsRef,
  walletCurrency,
  className,
  onOpenMetrics,
}: Props) {
  const { locale } = useRouter();
  const data = useFragment(ReferAFriendFragment, referralsRef);
  const shortLink = data.referralLink;

  // TODO: temporary calculation, replace rewardAmount and rewardCurrency with actual values from the backend once available
  const lifetimeSuccesses = data.referralHistory?.lifetime?.successes ?? 0;
  const lifetimeEarningsSubUnit = data.referralHistory?.lifetime?.earnings?.subUnitAmount ?? 0;
  const rewardCurrency =
    lifetimeEarningsSubUnit > 0 && data.referralHistory?.lifetime?.earnings?.currency
      ? data.referralHistory.lifetime.earnings.currency
      : (walletCurrency ?? getCurrencyFromLocale(locale));
  const rewardAmount =
    lifetimeSuccesses > 0 ? lifetimeEarningsSubUnit / lifetimeSuccesses / 100 : null;

  const shortenUrl = (url: string) => {
    const parts = new URL(url);
    return `${parts.hostname}${parts.pathname}`;
  };

  return (
    shortLink && (
      <Card className={clsx(classes.root, className)}>
        <div className={classes.container}>
          <ol className={classes.tutorialList}>
            <li>
              <Txt>
                Copy & paste your unique referral code into a text, email or social media post.
              </Txt>
            </li>
            <li>
              <Txt>
                Your friends need to click the link to sign up & successfully complete their first
                survey.
              </Txt>
            </li>
            <li>
              <Txt>
                Voila! You will both receive an instant{' '}
                {rewardAmount == null ? (
                  'reward'
                ) : (
                  <Currency amount={rewardAmount} currency={rewardCurrency} />
                )}
                .
              </Txt>
            </li>
          </ol>
          <Copyable
            clipboardValue={shortLink}
            displayValue={shortenUrl(shortLink)}
            title="Your unique referral code"
            className={classes.shortLink}
          />
          <hr className={classes.divider} />
          <Button
            buttonType="text"
            component="a"
            className={classes.referralLink}
            onClick={onOpenMetrics ?? undefined}
            onAuxClick={onOpenMetrics}
          >
            <Txt>Referral Tracking</Txt>
            <PopupIcon />
          </Button>
        </div>
      </Card>
    )
  );
}
